// Plan helpers — pure functions for plan logic (no DOM, no side effects).
// Spec: docs/superpowers/specs/2026-05-15-home-widgets-refactor-design.md
//
// Decisioni di design:
// - Mapping today→workout: A+C+B blended (calendar default smart + override
//   custom in profile.giorni_settimana_allenamento + sequence fallback se
//   l'utente salta giorni)
// - Concetto "settimana": ISO (Lunedì → Domenica, ora locale)
// - Streak stretching: grace-until-midnight (pattern Duolingo), soglia 100%
// - End-of-plan workout: hard banner dopo total_weeks
// - Stretch: perpetuo, riparte ogni settimana ISO
//
// Conventions:
// - exercise_key formato workout: `{fase}-{giorno}-{sezione}-{esercizio}`
//   (es. `0-0-2-4` = Fase 1 Giorno 1 Sezione 2 Esercizio 4)
// - exercise_key formato stretch: `s{giorno}-{sezione}-{esercizio}`
//   (es. `s0-0-3` = Giorno 0 Sezione 0 Esercizio 3)
// - item_key formato shopping: `{categoria}-{idx}` (es. `latticini-0`)

const WEEKS_PER_PHASE = 3; // standard piano workout (vedi v_plan_meta migration)

// ─── ISO Weekday & date helpers ───────────────────────────────────────────

// JS Date.getDay() → 0=Sun..6=Sat. Convertiamo a ISO 1=Lun..7=Dom.
export function getISOWeekday(date) {
  const d = new Date(date).getDay();
  return d === 0 ? 7 : d;
}

// Inizio settimana ISO (Lunedì 00:00 ora locale)
export function startOfISOWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const wd = getISOWeekday(d); // 1..7
  d.setDate(d.getDate() - (wd - 1));
  return d;
}

// Fine settimana ISO (Domenica 23:59:59.999)
export function endOfISOWeek(date) {
  const start = startOfISOWeek(date);
  start.setDate(start.getDate() + 7);
  start.setMilliseconds(-1);
  return start;
}

// {monday, sunday} per la settimana ISO che contiene `date`
export function getCurrentISOWeekRange(date) {
  return { monday: startOfISOWeek(date), sunday: endOfISOWeek(date) };
}

// Diff in days (>=0 se end>start)
export function daysBetween(start, end) {
  const s = new Date(start); s.setHours(0, 0, 0, 0);
  const e = new Date(end); e.setHours(0, 0, 0, 0);
  return Math.floor((e - s) / 86400000);
}


// ─── Training days (calendar + override) ──────────────────────────────────

// Smart default: dato N giorni_allenamento, restituisce array ISO weekdays
export function getDefaultTrainingDays(n) {
  if (!n || n < 1) return [];
  const presets = {
    1: [1],            // Lun
    2: [1, 4],         // Lun, Gio
    3: [1, 3, 5],      // Lun, Mer, Ven
    4: [1, 2, 4, 5],   // Lun, Mar, Gio, Ven
    5: [1, 2, 3, 4, 5],// Lun-Ven
    6: [1, 2, 3, 4, 5, 6], // Lun-Sab
    7: [1, 2, 3, 4, 5, 6, 7] // tutti
  };
  return presets[n] || [1, 3, 5]; // fallback 3gg
}

// Override-aware: legge profile.giorni_settimana_allenamento, fallback smart
export function getTrainingDays(profile) {
  if (!profile) return [];
  if (Array.isArray(profile.giorni_settimana_allenamento) && profile.giorni_settimana_allenamento.length > 0) {
    return profile.giorni_settimana_allenamento;
  }
  return getDefaultTrainingDays(profile.giorni_allenamento || 3);
}


// ─── Plan metadata (lato client, da data jsonb in memoria) ────────────────

// Start date del piano. Popolato in ui-onboarding loadPlans (da plans.created_at)
// e in generators.js dopo nuova generazione.
export function getPlanStartDate(plan) {
  if (!plan) return null;
  return plan._createdAt || plan.start_date || plan.created_at || null;
}

// Giorni elapsed dal start_date (>=0). null se no start_date.
export function daysSinceStart(plan, today) {
  const start = getPlanStartDate(plan);
  if (!start) return null;
  return Math.max(0, daysBetween(start, today));
}

// Settimana corrente del piano, 0-indexed (0..total_weeks-1)
export function getCurrentWeekOverall(plan, today) {
  const days = daysSinceStart(plan, today);
  if (days === null) return null;
  return Math.floor(days / 7);
}

// Fase corrente del piano workout:
// - Fase 0 = sett 0-2
// - Fase 1 = sett 3-5
// - 'end' se settimana >= total_weeks (es. 6 per 2 fasi × 3 sett)
export function getCurrentPhase(workoutPlan, today) {
  if (!Array.isArray(workoutPlan) || workoutPlan.length === 0) return 0;
  const week = getCurrentWeekOverall(workoutPlan, today);
  if (week === null) return 0;
  const phase = Math.floor(week / WEEKS_PER_PHASE);
  if (phase >= workoutPlan.length) return 'end';
  return phase;
}

// Settimana dentro la fase corrente (0..WEEKS_PER_PHASE-1)
export function getCurrentWeekInPhase(workoutPlan, today) {
  const week = getCurrentWeekOverall(workoutPlan, today);
  if (week === null) return 0;
  return week % WEEKS_PER_PHASE;
}


// ─── Completion filters ──────────────────────────────────────────────────

// Filter rows con completed_at IN settimana ISO corrente
export function filterCompletionsThisWeek(rows, today) {
  if (!Array.isArray(rows)) return [];
  const { monday, sunday } = getCurrentISOWeekRange(today);
  return rows.filter(r => {
    if (!r.completed_at) return false;
    const cAt = new Date(r.completed_at);
    return cAt >= monday && cAt <= sunday;
  });
}


// ─── Workout completion logic ────────────────────────────────────────────

// Restituisce gli exercise_key attesi per (phase, day)
// Formato: `{phaseIdx}-{dayIdx}-{sectionIdx}-{exerciseIdx}`
export function getWorkoutExpectedKeys(workoutPlan, phaseIdx, dayIdx) {
  const day = workoutPlan?.[phaseIdx]?.days?.[dayIdx];
  if (!day) return [];
  const keys = [];
  (day.sections || []).forEach((sec, sIdx) => {
    (sec.ex || []).forEach((_, eIdx) => {
      keys.push(`${phaseIdx}-${dayIdx}-${sIdx}-${eIdx}`);
    });
  });
  return keys;
}

// 100% soglia: TUTTI gli exercise_key attesi sono presenti nelle completions
// filtrate per settimana ISO corrente.
export function isWorkoutDayComplete(workoutPlan, completions, phaseIdx, dayIdx, today) {
  const expected = getWorkoutExpectedKeys(workoutPlan, phaseIdx, dayIdx);
  if (!expected.length) return false;
  const weekRows = filterCompletionsThisWeek(completions, today);
  const completedKeys = new Set(weekRows.map(r => r.exercise_key));
  return expected.every(k => completedKeys.has(k));
}

// Sessioni completate questa settimana ISO (distinct phase-day combo con 100%)
export function countWorkoutSessionsThisWeek(workoutPlan, completions, today) {
  if (!Array.isArray(workoutPlan)) return 0;
  let count = 0;
  for (let p = 0; p < workoutPlan.length; p++) {
    const days = workoutPlan[p]?.days || [];
    for (let d = 0; d < days.length; d++) {
      if (isWorkoutDayComplete(workoutPlan, completions, p, d, today)) {
        count++;
      }
    }
  }
  return count;
}


// ─── Today's workout (A+C+B blended) ─────────────────────────────────────

// ISO weekday di oggi → workout day index nella fase corrente.
// Se training days = [1,3,5] (Lun/Mer/Ven):
//   Lun (ISO 1) → day 0
//   Mer (ISO 3) → day 1
//   Ven (ISO 5) → day 2
// Restituisce day idx o null se oggi non è un training day.
export function getCalendarDayIdx(profile, today) {
  const trainingDays = getTrainingDays(profile);
  const todayISO = getISOWeekday(today);
  const idx = trainingDays.indexOf(todayISO);
  return idx === -1 ? null : idx;
}

// Sequence fallback: prossimo workout della fase corrente non completato
// questa settimana ISO. Day idx (0..n_days-1) o null se tutti completati.
export function getSequenceNextDayIdx(workoutPlan, completions, today) {
  const phase = getCurrentPhase(workoutPlan, today);
  if (phase === 'end') return null;
  const days = workoutPlan?.[phase]?.days || [];
  for (let dayIdx = 0; dayIdx < days.length; dayIdx++) {
    if (!isWorkoutDayComplete(workoutPlan, completions, phase, dayIdx, today)) {
      return dayIdx;
    }
  }
  return null; // all complete this week
}

// Compose A+C+B per il widget Oggi:
// 1. Phase === 'end' → end-state (banner "Piano completato")
// 2. Calendar default + override (A+C): oggi è training day?
//    - Sì + day non ancora completato → restituisci quel workout (mode: 'calendar')
//    - Sì + già completato → check sequence fallback
//    - No (oggi non è training day) → check sequence fallback (B)
// 3. Sequence fallback (B): workout pendenti questa settimana?
//    - Sì → restituisci il primo pendente (mode: 'sequence')
//    - No → 'rest' (sei a posto questa settimana)
//
// Restituisce uno di:
//   { kind: 'workout', phaseIdx, dayIdx, dayData, mode: 'calendar'|'sequence' }
//   { kind: 'rest',    message: string }
//   { kind: 'end',     message: string }
//   { kind: 'empty',   message: string } se piano non generato
export function getTodayWorkout(workoutPlan, profile, completions, today) {
  if (!Array.isArray(workoutPlan) || workoutPlan.length === 0) {
    return { kind: 'empty', message: 'piano non generato' };
  }
  const phase = getCurrentPhase(workoutPlan, today);
  if (phase === 'end') {
    return { kind: 'end', message: 'Piano completato' };
  }

  const phaseObj = workoutPlan[phase];
  const days = phaseObj?.days || [];

  // (1) Calendar lookup
  const calIdx = getCalendarDayIdx(profile, today);
  if (calIdx !== null && calIdx < days.length) {
    if (!isWorkoutDayComplete(workoutPlan, completions, phase, calIdx, today)) {
      return { kind: 'workout', phaseIdx: phase, dayIdx: calIdx, dayData: days[calIdx], mode: 'calendar' };
    }
  }

  // (2) Sequence fallback
  const seqIdx = getSequenceNextDayIdx(workoutPlan, completions, today);
  if (seqIdx !== null) {
    return { kind: 'workout', phaseIdx: phase, dayIdx: seqIdx, dayData: days[seqIdx], mode: 'sequence' };
  }

  // (3) Tutti completati questa settimana
  return { kind: 'rest', message: 'Settimana completata 🎉' };
}


// ─── Stretch completion + streak ─────────────────────────────────────────

// exercise_key per stretch: `s{dayIdx}-{sectionIdx}-{exerciseIdx}`
// Confermato in ui-plans.js _sRen (linea ~411).
export function getStretchExpectedKeys(stretchPlan, dayIdx) {
  const day = stretchPlan?.[dayIdx];
  if (!day) return [];
  const secs = (day.sections && day.sections.length) ? day.sections : [{ ex: day.ex || [] }];
  const keys = [];
  secs.forEach((sec, sIdx) => {
    (sec.ex || []).forEach((_, eIdx) => {
      keys.push(`s${dayIdx}-${sIdx}-${eIdx}`);
    });
  });
  return keys;
}

// 100% completion del day stretch su targetDate (ora locale).
export function isStretchDayCompleteOn(stretchPlan, completions, dayIdx, targetDate) {
  const expected = getStretchExpectedKeys(stretchPlan, dayIdx);
  if (!expected.length) return false;
  const tStart = new Date(targetDate); tStart.setHours(0, 0, 0, 0);
  const tEnd = new Date(targetDate); tEnd.setHours(23, 59, 59, 999);
  const matching = (completions || []).filter(r => {
    if (!r.exercise_key || !r.exercise_key.startsWith(`s${dayIdx}-`)) return false;
    const cAt = new Date(r.completed_at);
    return cAt >= tStart && cAt <= tEnd;
  });
  const completedKeys = new Set(matching.map(r => r.exercise_key));
  return expected.every(k => completedKeys.has(k));
}

// Mapping data → day idx del piano stretch.
// Convention: piano stretch ha 7 days. Day 0 = Lunedì, ..., Day 6 = Domenica.
export function getStretchDayIdxFor(date) {
  return getISOWeekday(date) - 1; // ISO 1..7 → 0..6
}

// Streak Duolingo-style: N giorni consecutivi completati al 100%, terminanti
// con ieri o oggi.
//
// Restituisce { streak: N, completedToday: bool, completedYesterday: bool }
export function computeStretchStreak(stretchPlan, completions, today) {
  if (!Array.isArray(stretchPlan) || stretchPlan.length === 0) {
    return { streak: 0, completedToday: false, completedYesterday: false };
  }
  const todayD = new Date(today); todayD.setHours(0, 0, 0, 0);
  const yesterday = new Date(todayD); yesterday.setDate(yesterday.getDate() - 1);

  const completedToday = isStretchDayCompleteOn(stretchPlan, completions, getStretchDayIdxFor(todayD), todayD);
  const completedYesterday = isStretchDayCompleteOn(stretchPlan, completions, getStretchDayIdxFor(yesterday), yesterday);

  // Tail: today se completato oggi, ieri se completato ieri, altrimenti streak=0
  let tail;
  if (completedToday) tail = new Date(todayD);
  else if (completedYesterday) tail = new Date(yesterday);
  else return { streak: 0, completedToday, completedYesterday };

  // Backward walk
  let streak = 0;
  const cursor = new Date(tail);
  while (true) {
    if (isStretchDayCompleteOn(stretchPlan, completions, getStretchDayIdxFor(cursor), cursor)) {
      streak++;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
    // Safety cap: max 365 giorni per evitare infinite loop su completions buggy
    if (streak > 365) break;
  }
  return { streak, completedToday, completedYesterday };
}


// ─── Shopping list helpers ───────────────────────────────────────────────

// Flatten shopping items in array di {key, name, qty, unit, category}
// Lista può essere uno di 3 shape:
//   1. Array di group: [{n: 'category_name', items: [...]}]  ← formato reale generatore
//   2. Object categorized: {latticini: [items], cereali: [items]}
//   3. Array flat: [item1, item2, ...]
//
// item_key formato stabile: `{category}-{idx}` (es. 'latticini-0').
// Stesso formato usato dall'iframe shopping in coach/src/ui-plans.js
// per persistenza in shopping_completions table.
export function flattenShoppingItems(shoppingPlan) {
  if (!shoppingPlan) return [];
  const lista = shoppingPlan.lista || shoppingPlan.items || shoppingPlan;
  const out = [];
  const pushItem = (item, idx, cat) => {
    const name = typeof item === 'string' ? item : (item?.name || item?.nome || item?.alimento || JSON.stringify(item));
    out.push({
      key: `${cat}-${idx}`,
      name,
      qty: item?.qty || item?.quantity || item?.q,
      unit: item?.unit || item?.u,
      category: cat
    });
  };
  if (Array.isArray(lista)) {
    // Detect: array di group (es. [{n, items}]) vs flat items
    if (lista.length && lista[0] && typeof lista[0] === 'object' && Array.isArray(lista[0].items)) {
      lista.forEach(group => {
        const cat = group.n || 'altri';
        (group.items || []).forEach((item, idx) => pushItem(item, idx, cat));
      });
    } else {
      lista.forEach((item, idx) => pushItem(item, idx, 'flat'));
    }
  } else if (lista && typeof lista === 'object') {
    Object.entries(lista).forEach(([cat, items]) => {
      if (Array.isArray(items)) {
        items.forEach((item, idx) => pushItem(item, idx, cat));
      }
    });
  }
  return out;
}

// Conta articoli totali nella lista spesa
export function totalShoppingItems(shoppingPlan) {
  return flattenShoppingItems(shoppingPlan).length;
}

// Conta articoli spuntati (matching item_key in shopping_completions filtered by valid items)
export function countShoppingCompletions(shoppingPlan, shoppingCompletions) {
  if (!Array.isArray(shoppingCompletions)) return 0;
  const validKeys = new Set(flattenShoppingItems(shoppingPlan).map(i => i.key));
  return shoppingCompletions.filter(c => validKeys.has(c.item_key)).length;
}


// ─── Meal completion counts ──────────────────────────────────────────────

// Conta pasti completati questa settimana ISO
// meal_completions schema: {day_index, slot, item_index, completed_at}
export function countMealsThisWeek(mealCompletions, today) {
  return filterCompletionsThisWeek(mealCompletions || [], today).length;
}

// Conta totale pasti nel piano alimentare (sum di m[] su tutti days)
export function totalMealsInPlan(mealPlan) {
  if (!mealPlan?.days || !Array.isArray(mealPlan.days)) return 0;
  return mealPlan.days.reduce((sum, d) => {
    if (!d?.m) return sum;
    if (Array.isArray(d.m)) return sum + d.m.length;
    return sum + Object.keys(d.m).length;
  }, 0);
}

// Verifica end-of-plan per meal: total_weeks settimane dopo start_date
// (total_weeks viene da profile.durata_dieta in v_plan_meta).
export function isMealPlanEnded(mealPlan, durataDietaSettimane, today) {
  const week = getCurrentWeekOverall(mealPlan, today);
  if (week === null) return false;
  return week >= durataDietaSettimane;
}
